import { AfterContentInit, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UIChart } from 'primeng/chart';
import { DwChartConfig, DwChartPreferences, DwChartTypes } from '../../models/dw-chart-preferences.model';
import { DwChartBuilderService } from '../../services/dw-chart-builder.service';
import {
  DwContainerOptions,
  DwGridColumnPreference,
  DwGridEvent,
  DwGridEventType,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQuery,
  DwQueryMdAttribute
} from '@devwareapps/devware-cap';
import { DwChartDataSelectedEvent } from '../../models/dw-chart-data-selected-event.model';
@Component({
  selector: 'dw-chart-tile',
  templateUrl: './dw-chart-tile.component.html',
  styleUrls: ['./dw-chart-tile.component.scss']
})
export class DwChartTileComponent implements OnInit, OnChanges, AfterContentInit {
  private readonly totalRowFlagField = '__dwIsTotalRow';
  private metadataLabelRequestId = 0;
  private screenMaintainAspectRatio: boolean;

  @Input() chartPreferences: DwChartPreferences
  @Input() data: any[];
  @Input() gridSourceData: any[] | null = null;
  @Input() containerOptions : DwContainerOptions;
  
  @Input() query: DwQuery;
  
  @Input() additionalText: string;
  
  @Output() dataSelected = new EventEmitter<DwChartDataSelectedEvent>();
  @ViewChild(UIChart) chartComponent: UIChart;
  
  chartConfig: DwChartConfig;
  gridPreferences: any;
  hideToggle = false;

  finalContainerOptions : DwContainerOptions;
  columnPreferences: DwGridColumnPreference[];
  gridData: any[] = [];
  gridValueSuffixes: { [field: string]: string } = {};

  constructor(
    private dwChartBuilderService: DwChartBuilderService,
    @Inject(DwMetaDataServiceToken) private metaDataService: DwMetaDataService
  ) { }

  showChart = true;
  ngOnChanges(changes: SimpleChanges) {
    this.setupChart();
    this.setupContainer();
  }

  ngOnInit(): void {
    this.setupChart();
    this.setupContainer();
  }

  ngAfterContentInit(): void {
  }

  toggleShowChart() {
    this.showChart = !this.showChart;
  }

  formatGridValue(value: any, field: string): any {
    if (value === null || value === undefined || value === '') {
      return value;
    }

    const suffix = this.gridValueSuffixes[field];
    const displayValue = value.toString();
    return suffix && !displayValue.endsWith(suffix)
      ? `${displayValue}${suffix}`
      : displayValue;
  }

  resizeChart(forPrint = false): void {
    if (!this.chartComponent?.chart) {
      return;
    }

    if (forPrint) {
      if (this.screenMaintainAspectRatio === undefined) {
        this.screenMaintainAspectRatio = this.chartComponent.chart.options.maintainAspectRatio;
      }
      this.chartComponent.chart.options.maintainAspectRatio = false;
    } else if (this.screenMaintainAspectRatio !== undefined) {
      this.chartComponent.chart.options.maintainAspectRatio = this.screenMaintainAspectRatio;
      this.screenMaintainAspectRatio = undefined;
    }

    this.chartComponent.chart.resize();
    this.chartComponent.chart.update(0);
  }

  onDataSelect(event: any) {
    const itemIndex = event?.element?._index;
    const datasetIndex = event?.element?._datasetIndex;

    if (itemIndex>=0) {
      const datset = event?.dataset[itemIndex];
      const dataItem = this.data[itemIndex];
      const series = this.chartPreferences.series[datasetIndex];

      const dwEvent: DwChartDataSelectedEvent = {
        data: dataItem,
        series: series,
        dataValue: dataItem[series.dataField],
        category: dataItem[this.chartPreferences.categoryField],
        categoryValue: dataItem[this.chartPreferences.categoryValueField],
        isDoubleClick: event?.originalEvent?.detail >= 2,
        browserEvent: event.originalEvent
      };

      this.dataSelected.emit(dwEvent);
    }
  }

  
  onGridEvent(event: DwGridEvent) {

    if (event.eventType !== DwGridEventType.gridRowClick && event.eventType !== DwGridEventType.gridRowDoubleClick) {
      return;
    }
    const browserEvent = event?.browserEvent as MouseEvent | PointerEvent | undefined;

    this.emitGridDataSelected(
      event.eventData,
      event?.eventColumn?.field,
      browserEvent,
      event.eventType === DwGridEventType.gridRowDoubleClick || (browserEvent?.detail || 0) >= 2
    );
  }

  onSnapshotGridCellSelected(dataItem: any, eventField: string, browserEvent: MouseEvent | KeyboardEvent): void {
    this.emitGridDataSelected(
      dataItem,
      eventField,
      browserEvent,
      browserEvent instanceof MouseEvent && browserEvent.detail >= 2
    );
  }

  private emitGridDataSelected(
    dataItem: any,
    eventField: string,
    browserEvent: MouseEvent | PointerEvent | KeyboardEvent | undefined,
    isDoubleClick: boolean
  ): void {
    if (!dataItem || dataItem[this.totalRowFlagField]) {
      return;
    }

    const fallbackSeries = this.chartPreferences?.series?.[0] || null;
    const series = this.chartPreferences?.series?.find(s => s.dataField === eventField) || fallbackSeries;
    const dataField = series?.dataField || eventField;

    if (!series || !dataField) {
      return;
    }

    const dataValue = dataItem[dataField];

    const dwEvent: DwChartDataSelectedEvent = {
      data: dataItem,
      series: series,
      dataValue: dataValue,
      category: dataItem[this.chartPreferences.categoryField],
      categoryValue: dataItem[this.chartPreferences.categoryValueField],
      isDoubleClick: isDoubleClick,
      browserEvent: browserEvent as PointerEvent,
    };

    this.dataSelected.emit(dwEvent);
  }


  private setupChart() {
    if (!this.chartPreferences || !this.data) {
      return;
    }

    const canShowGridFirst = !!this.chartPreferences.showGridFirst
      && (!!this.chartPreferences.gridPreferences?.useChartDataForGrid || !!this.query);

    this.showChart = !((this.data.length === 1 && this.chartPreferences.chartType != DwChartTypes.pie) || canShowGridFirst);

    if (this.chartPreferences.hideChartDataToggle) {
      this.hideToggle = true;
    } else {
      this.hideToggle = false;
    }

    this.chartConfig = this.dwChartBuilderService.buildChartConfig(this.chartPreferences, this.data);

    this.gridPreferences = this.dwChartBuilderService.buildGridPreferences(this.chartPreferences, this.query);

    this.columnPreferences = this.gridPreferences.views[0].columnPreferences;
    this.gridValueSuffixes = (this.chartPreferences.series || []).reduce((suffixes, series) => {
      if (series.dataField && series.valueSuffix) {
        suffixes[series.dataField] = series.valueSuffix;
      }
      return suffixes;
    }, {} as { [field: string]: string });
    this.applyMetadataColumnLabels();
    this.gridData = this.buildGridData();
  }

  private applyMetadataColumnLabels(): void {
    const itemName = this.query?.RootTable || this.chartPreferences?.item;
    const requestId = ++this.metadataLabelRequestId;

    if (!itemName || !this.columnPreferences?.length) {
      return;
    }

    this.metaDataService.getSavedQueryMetaData(itemName, null, null, true).subscribe(queryMetaData => {
      if (requestId !== this.metadataLabelRequestId) {
        return;
      }

      const attributes = queryMetaData?.Attributes || [];
      const columnPreferences = this.columnPreferences.map(columnPreference =>
        this.applyMetadataColumnLabel(columnPreference, attributes));

      this.columnPreferences = columnPreferences;
      this.gridPreferences.views[0].columnPreferences = columnPreferences;
    });
  }

  private applyMetadataColumnLabel(
    columnPreference: DwGridColumnPreference,
    attributes: DwQueryMdAttribute[]
  ): DwGridColumnPreference {
    if (!columnPreference?.field) {
      return columnPreference;
    }

    const fieldName = this.getUnqualifiedFieldName(columnPreference.field);
    const hasExplicitLabel = !!columnPreference.label
      && columnPreference.label !== columnPreference.field
      && columnPreference.label !== fieldName;

    if (hasExplicitLabel) {
      return columnPreference;
    }

    const attribute = attributes.find(item =>
      item.AttributeName === fieldName || item.DisplayAttributeName === fieldName);
    const metadataLabel = attribute?.AttributeDisplay;

    if (!metadataLabel) {
      return columnPreference;
    }

    return {
      ...columnPreference,
      label: metadataLabel,
      columnDisplay: columnPreference.columnDisplay || metadataLabel
    };
  }

  private getUnqualifiedFieldName(fieldName: string): string {
    const parts = fieldName.split('.');
    return parts[parts.length - 1];
  }

  private buildGridData(): any[] {
    const sourceData = this.gridSourceData || this.data;

    if (!sourceData?.length) {
      return [];
    }

    if (!this.chartPreferences?.gridPreferences?.showTotalsRow) {
      return sourceData;
    }

    const totalRow = this.buildTotalsRow(sourceData);

    return totalRow ? [...sourceData, totalRow] : sourceData;
  }

  private buildTotalsRow(sourceData: any[]): any | null {
    const seriesFields = new Set((this.chartPreferences?.series || []).map(series => series.dataField).filter(field => !!field));

    if (seriesFields.size === 0) {
      return null;
    }

    const totalRow: any = {
      [this.totalRowFlagField]: true
    };

    const labelField = this.getTotalLabelField(seriesFields);

    if (labelField) {
      totalRow[labelField] = this.chartPreferences?.gridPreferences?.totalRowLabel || 'Total';
    }

    for (const columnPreference of this.columnPreferences || []) {
      if (!columnPreference?.field || columnPreference.field === labelField) {
        continue;
      }

      const numericValues = sourceData
        .map(row => row?.[columnPreference.field])
        .filter(value => value !== null && value !== undefined && value !== '' && !isNaN(Number(value)));

      totalRow[columnPreference.field] = numericValues.length > 0
        ? numericValues.reduce((sum, value) => sum + Number(value), 0)
        : '';
    }

    for (const seriesField of Array.from(seriesFields)) {
      totalRow[seriesField] = sourceData.reduce((sum, row) => {
        const value = Number(row?.[seriesField]);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
    }

    return totalRow;
  }

  private getTotalLabelField(seriesFields: Set<string>): string | null {
    const firstNonSeriesColumn = (this.columnPreferences || [])
      .find(columnPreference => !!columnPreference?.field && !seriesFields.has(columnPreference.field));

    return firstNonSeriesColumn?.field || this.chartPreferences?.categoryField || null;
  }


  private setupGrid() {
  }

  private setupContainer() {
    this.finalContainerOptions = {
      useContainerStyling: true,
      styleName: 'ENHANCED',
      containerAllowCollapse: false,
      ...this.containerOptions,
      showTitle: this.chartPreferences?.showContainerTitle,
    };
  }

  

}
